// WorldChunkCoords.js - 大地图区块坐标换算
import { GAME_CONSTANTS } from './GameConstants.js';

/**
 * @param {Object} meta - world_meta.json
 * @param {number} globalTileX
 * @param {number} globalTileY
 * @returns {{ gridX: number, gridY: number, localTileX: number, localTileY: number }}
 */
export function globalTileToChunk(meta, globalTileX, globalTileY) {
    const chunkW = meta.chunkWidth;
    const chunkH = meta.chunkHeight;

    const gridX = Math.floor(globalTileX / chunkW);
    const gridY = Math.floor(globalTileY / chunkH);

    return {
        gridX,
        gridY,
        localTileX: globalTileX - gridX * chunkW,
        localTileY: globalTileY - gridY * chunkH
    };
}

/**
 * @param {Object} meta
 * @param {number} gridX
 * @param {number} gridY
 * @param {number} localTileX
 * @param {number} localTileY
 */
export function chunkTileToGlobal(meta, gridX, gridY, localTileX, localTileY) {
    return {
        globalTileX: gridX * meta.chunkWidth + localTileX,
        globalTileY: gridY * meta.chunkHeight + localTileY
    };
}

/**
 * @param {Object} meta
 * @param {number} gridX
 * @param {number} gridY
 * @returns {{ offsetX: number, offsetY: number }}
 */
export function getChunkPixelOffset(meta, gridX, gridY) {
    const tileSize = GAME_CONSTANTS.TILE_SIZE;
    return {
        offsetX: gridX * meta.chunkWidth * tileSize,
        offsetY: gridY * meta.chunkHeight * tileSize
    };
}

/**
 * @param {Object} meta
 * @param {number} pixelX
 * @param {number} pixelY
 */
export function pixelToGlobalTile(meta, pixelX, pixelY) {
    const tileSize = GAME_CONSTANTS.TILE_SIZE;
    return {
        globalTileX: Math.floor((pixelX - tileSize / 2) / tileSize),
        globalTileY: Math.floor((pixelY - tileSize / 2) / tileSize)
    };
}

/**
 * @param {Object} meta
 * @param {number} globalTileX
 * @param {number} globalTileY
 * @returns {string}
 */
export function getChunkKeyFromTile(meta, globalTileX, globalTileY) {
    const { gridX, gridY } = globalTileToChunk(meta, globalTileX, globalTileY);
    return `${gridX}_${gridY}`;
}

/**
 * @param {Object} meta
 * @param {number} pixelX
 * @param {number} pixelY
 * @param {number} [radius=1]
 * @returns {Array<{ gridX: number, gridY: number }>}
 */
export function getChunkCoordsInRadius(meta, pixelX, pixelY, radius = 1) {
    const { globalTileX, globalTileY } = pixelToGlobalTile(meta, pixelX, pixelY);
    const center = globalTileToChunk(meta, globalTileX, globalTileY);
    const coords = [];

    for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
            const gridX = center.gridX + dx;
            const gridY = center.gridY + dy;

            if (gridX < 0 || gridY < 0 || gridX >= meta.gridCols || gridY >= meta.gridRows) {
                continue;
            }

            coords.push({ gridX, gridY });
        }
    }

    return coords;
}

/**
 * @param {Object} meta
 * @param {number} gridX
 * @param {number} gridY
 * @returns {Object|null}
 */
export function findChunkEntry(meta, gridX, gridY) {
    return (meta.chunks || []).find((chunk) => chunk.gridX === gridX && chunk.gridY === gridY) || null;
}

/**
 * @param {Object} spawn - world_meta.defaultSpawn
 * @param {Object} meta
 */
export function resolveGlobalSpawn(spawn, meta) {
    if (!spawn) {
        return { globalTileX: 0, globalTileY: 0 };
    }

    if (spawn.gridX !== undefined && spawn.gridY !== undefined) {
        return chunkTileToGlobal(meta, spawn.gridX, spawn.gridY, spawn.tileX || 0, spawn.tileY || 0);
    }

    return {
        globalTileX: spawn.tileX || 0,
        globalTileY: spawn.tileY || 0
    };
}
